import { AdminNavigation } from "@/components/AdminNavigation";
import { createRootRoute, Outlet, useLocation } from "@tanstack/react-router";
import { Navigation } from "@/components/Navigation";
import { DATENSCHUTZ_URL, IMPRESSUM_URL } from "@/lib/rechtliches";

/*
 * App-Shell. Alles, was auf jeder Seite steht: Skip-Link, Kopf, Inhalts-
 * container, Fuß.
 *
 * ═══ Warum TanStack Router (und file-based routing) ═══════════════════════
 *
 * In dieser App IST die URL der Zustand. Filter, Seite, Sortierung und
 * Wert-Modus leben ausnahmslos in Query-Params — eine Suche muss als Link
 * teilbar, als Lesezeichen ablegbar und per Zurück-Taste rückgängig sein.
 * Im Altsystem trug `parseSearchFiltersFromParams` diese Last von Hand:
 * 13 Filterdimensionen aus `URLSearchParams` lesen, einzeln validieren,
 * Defaults setzen — ungetypt, und jeder neue Parameter eine weitere Stelle
 * zum Vergessen.
 *
 * TanStack Router validiert Search-Params typisiert (`validateSearch` pro
 * Route). Der Router ist damit das direkte Gegenstück zu jener Funktion,
 * nur mit dem Typ als Vertrag: ein Parameter, den die Route nicht kennt,
 * kommt gar nicht erst in der Komponente an, und `Link`-Ziele werden gegen
 * dasselbe Schema geprüft. Das ist der Grund für die Wahl — nicht die
 * Datei-Konvention.
 *
 * File-based routing (`src/routes/`) kommt obendrauf, weil der Routenbaum
 * dann aus dem Verzeichnis ablesbar ist statt aus einer zentralen Liste,
 * die niemand pflegt. `routeTree.gen.ts` erzeugt das Vite-Plugin — die
 * Datei ist Generat und wird nicht von Hand angefasst.
 */

const BUILD_ZEIT = new Date(__BUILD_TIME__);
const IST_PRODUKTION = import.meta.env.PROD;

/*
 * Die beiden Pflichtverweise im Fuß.
 *
 * ═══ Warum sie hier stehen und nicht in einer Sicht ══════════════════════
 *
 * § 5 DDG verlangt „leicht erkennbar und unmittelbar erreichbar", Art. 13
 * DSGVO die Information bei der Erhebung. Beides ist eine Aussage über JEDE
 * Seite, nicht über eine. Der Fuß der Shell ist die einzige Stelle dieser
 * SPA, die das ohne eine zweite Fassung leisten kann — dieselbe Entscheidung
 * wie im Bestand, dessen `rechtliches.ts` als Konsumenten ausdrücklich den
 * Layout-Fuß nennt.
 *
 * Sie hängen an KEINER Sitzung. Der Kopf darüber ist rollenabhängig und weiß
 * beim ersten Bild noch gar nicht, wer da ist; der Fuß weiß es nie und muss
 * es nicht. Die Anmeldeseite ist die erste Seite, die ein Mensch sieht, und
 * genau dort dürfen die Angaben nicht fehlen.
 *
 * ═══ Warum eigene Klassen und nicht `KNOPF_BASIS` ════════════════════════
 *
 * Dieselbe Abwägung wie beim Abmelde-Knopf in `Navigation.tsx`, nur
 * andersherum: dort trug `KNOPF_BASIS` einen schwarzen Fokusring auf
 * schwarzem Grund. Hier ist der Grund hell, der schwarze Ring also richtig —
 * aber es sind LINKS und keine Knöpfe. Ein Knopf-Aussehen versprüche eine
 * Handlung in dieser Anwendung; diese beiden verlassen sie.
 *
 * `underline` ist deshalb keine Zier: im Fuß steht Text (Produktname,
 * Build-Zeit) unmittelbar daneben, und die Unterstreichung ist das einzige
 * Merkmal, das einen Verweis ohne Farbunterschied als solchen ausweist —
 * `text-muted` tragen die Nachbarn auch.
 *
 * `min-h-11` ist die 44-px-Hausregel für klickbare Elemente (AGENTS.md
 * § a11y-Defaults, dieselbe Zahl wie in `KNOPF_BASIS` und `PUNKT_KLASSEN`).
 * Sie kostet hier sichtbare Höhe im Fuß — und ist genau deshalb richtig: das
 * Telefon ist das Gerät, auf dem diese beiden Verweise am schwersten zu
 * treffen sind.
 */
const RECHTLICHES_KLASSEN =
  "inline-flex min-h-11 items-center rounded-sm underline underline-offset-2 transition-colors hover:text-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-black";

export const Route = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const pfad = useLocation({ select: (ort) => ort.pathname });
  const einstieg = pfad === "/login" || pfad === "/";
  const administration = pfad === "/admin" || pfad.startsWith("/admin/");
  return (
    <div className="flex min-h-full flex-1 flex-col">
      <a
        href="#inhalt"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-brand-black focus:px-4 focus:py-2 focus:text-on-brand focus:outline-2 focus:outline-offset-2 focus:outline-accent"
      >
        Zum Inhalt springen
      </a>

      {/*
        * ═══ Warum die Kopfzeile UMBRICHT (Posten 57) ════════════════════
        *
        * Sie war ein `flex` ohne `flex-wrap`. Damit ergab sich aus den
        * Mindestbreiten der Kinder eine Mindestbreite des Kopfes, und die
        * hing am Zugang — GEMESSEN, auf 320 px:
        *
        *     ohne Sitzung   338 px   innerster Knoten: a „Anmelden"
        *     eine Sicht     406 px   innerster Knoten: button „Abmelden"
        *     Administrator  827 px   fünf Punkte, Name und Abmelden
        *
        * Jeder schmalere Viewport erzeugte waagerechten DOKUMENT-Scroll —
        * 507 px als Administrator auf 320 px. Nicht die Tabellen: die
        * scrollen in ihrem eigenen `overflow-x-auto` und sind unschuldig.
        *
        * ═══ Umbruch und nicht Schubfach — eine bewusste Verkleinerung ═══
        *
        * Der Bestand löst es mit `hidden md:flex` plus Schubfach
        * (`MobileMenu.tsx`). Hier steht `flex-wrap`, und das ist eine
        * Entscheidung mit drei Gründen:
        *
        *   1. Ein Schubfach nimmt etwas WEG und gibt es hinter einem Knopf
        *      zurück. `display:none` entfernt den Knoten aus dem
        *      Zugänglichkeitsbaum; wer das baut, schuldet Fokusfang,
        *      Escape, Klick-daneben und Schließen bei Navigation. Vier neue
        *      Zustände für ein Problem, das die Zeile selbst lösen kann.
        *   2. `flex-wrap` kann strukturell nicht überlaufen: kein Kind
        *      zwingt die Zeile mehr über die Breite des Behälters. Der
        *      Kopf wird höher statt breiter, und höher ist auf einem
        *      Telefon die richtige Richtung.
        *   3. Im KOPF blendet keine Stelle etwas nach Breite aus. Ein
        *      Schubfach wäre hier die erste Ausnahme gewesen — für die
        *      Navigation, also für den Weg durch die Anwendung.
        *      Was in dieser SPA sonst nach Breite ausblendet, führt
        *      `components/Breitenwechsel.tsx`; die Aussage steht dort und
        *      wird hier nicht wiederholt.
        *
        * Was das NICHT ist: eine Zusage über die Höhe. Der Kopf wird auf
        * 320 px mehrzeilig, und `e2e/specs/kopf-mobil.spec.ts` sichert
        * ausdrücklich nur zu, dass das Dokument nicht seitwärts scrollt.
        *
        * `gap-y-2` ist der Umbruchabstand: ohne ihn kleben die Zeilen
        * aneinander, weil `gap-3` bis hierher nur waagerecht wirken musste.
        */}
      {/*
        * ═══ Die gelbe Kante und warum sie am KOPF sitzt ══════════════════
        *
        * Der Bestand trägt sie („border-b-4 border-accent" in `Header.tsx`)
        * und sie ist das einzige Stück Marke, das auf JEDER Sicht zu sehen
        * ist: die Wortmarke ist klein, der Rest der Seite weiss. Ohne sie
        * endet der schwarze Balken auf Weiss und der Kopf wirkt wie ein
        * Fremdkörper — der Betreiber hat genau das zweimal „schlechter als
        * der Bestand" genannt.
        *
        * Der Kopf bleibt wie im Bestand beim Scrollen sichtbar. `z-30` liegt
        * über den Tabellenköpfen (`z-10`), deren `top-16` wiederum exakt an
        * die 64 px hohe Kopfleiste anschliesst.
        */}
      <header className="sticky top-0 z-30 border-b-4 border-accent bg-brand-black text-on-brand">
        <div className="mx-auto flex h-16 max-w-5xl items-center gap-3 px-4 lg:max-w-[96rem]">
          {/*
           * ═══ DAS ECHTE LOGO, nicht mehr eine gesetzte Wortmarke ═════════
           *
           * Hier stand `LINNEN<span class="text-accent">BECKER</span>` — die
           * Marke als Text, in Versalien, mit gelber zweiter Silbe. Das war
           * eine ERFINDUNG dieses Ports: der Bestand zeigt an derselben Stelle
           * das Bildzeichen (`<Logo variant="yellow-on-dark" size="md">` in
           * `Header.tsx`), also das gelbe Hauszeichen mit dem Schriftzug
           * „Linnenbecker" in gemischter Schrift.
           *
           * Der Betreiber hat es gemeldet („es fehlt noch das richtige Logo"),
           * und es ist der sichtbarste Unterschied überhaupt: die Marke steht
           * auf JEDER Sicht oben links.
           *
           * ═══ Die Datei kommt aus dem Bestand und ist dieselbe ═══════════
           *
           * `web/public/brand/logo-yellow.png`, 2608 × 728, 20 kB, aus
           * `linnenbecker_altware/web/public/brand/` übernommen. Kein
           * nachgezeichnetes SVG: eine selbstgebaute Kurve wäre eine zweite
           * Fassung der Marke, und sie liefe von der echten weg.
           *
           * Der Schriftzug im Bild ist WEISS und das Hauszeichen gelb — auf
           * dem schwarzen Balken trägt beides. Genau deshalb heisst die Datei
           * `-yellow` und nicht `-dark`: sie ist die Fassung FÜR dunklen
           * Grund, und auf Weiss wäre der Schriftzug unsichtbar. Wer sie
           * einmal auf hellem Grund braucht, braucht eine andere Datei — der
           * Bestand hat dafür keine und fällt dort auf gesetzten Text zurück.
           *
           * ═══ `width`/`height` sind Pflicht und kein Zierrat ═════════════
           *
           * Sie halten den Platz, bevor das Bild geladen ist. Ohne sie ist der
           * Kopf im ersten Bild 32 px flacher und springt danach — genau der
           * Layout-Shift, gegen den das `min-h-11` am `<nav>` schon
           * argumentiert (`Navigation.tsx`). 115 = 2608 ÷ 728 × 32, also das
           * Seitenverhältnis der Datei.
           *
           * `shrink-0`: die Kopfzeile ist `flex-wrap`, und ein Bild ohne das
           * würde bei Platzmangel gestaucht statt umgebrochen.
           *
           * ═══ Was dabei VERLOREN geht, benannt statt verschwiegen ════════
           *
           * Der Text war unzerstörbar. Ein Bild kann fehlen — dann steht dort
           * nur noch der `alt`-Text, und die Marke ist weg. Der Bestand trägt
           * dieselbe Blösse; ein zusätzlicher Text-Rückfall wäre eine zweite
           * Fassung der Marke, die im Normalfall niemand sieht.
           */}
          <img
            src="/brand/logo-yellow.png"
            alt="Linnenbecker"
            width={115}
            height={32}
            className="h-8 w-auto shrink-0"
          />
          {/*
           * Versalien und Sperrung wie im Bestand: der Zusatz ist eine
           * Bereichsangabe neben der Wortmarke und keine zweite Marke. In
           * gemischter Schrift und `text-sm` stand er zeichengleich neben
           * „LINNENBECKER" und sah aus wie ein zweites Wort desselben Namens.
           */}
          <span className="sr-only text-[11px] tracking-wider text-on-brand/70 uppercase sm:not-sr-only">
            Altwaren-Suche
          </span>

          {/*
           * Navigation, jetzt rollenabhängig: die Punkte kommen aus
           * `me.sichten` (`GET /api/v1/me`) — serverseitig gefiltert, in
           * Navigations-Reihenfolge. Hier stand bis zuletzt `/controlling`
           * fest verdrahtet für ALLE; mit jeder weiteren Sicht wäre daraus ein
           * Punkt geworden, der für manche Rollen ins 403 führt.
           *
           * Die Shell hält davon bewusst nichts: Zustände (Laden, 401, keine
           * Sicht, Störung), Reihenfolge und der Abgleich mit dem Routenbaum
           * liegen in `components/Navigation.tsx`. Das Layout des Kopfes
           * bleibt hier, die Frage „wer darf was sehen" gehört dorthin — und
           * beantwortet wird sie ohnehin vom Server.
           */}
          <Navigation />
        </div>
      </header>

      <main
        id="inhalt"
        tabIndex={-1}
        className={einstieg
          ? "relative flex w-full flex-1 items-center justify-center bg-linear-to-b from-surface-soft via-background to-background px-4 py-10 focus:outline-none"
          : `mx-auto w-full max-w-5xl flex-1 px-4 py-8 focus:outline-none ${
              administration ? "" : "lg:max-w-[96rem]"
            }`}
      >
        <AdminNavigation />
        <Outlet />
      </main>

      <footer className="border-t border-line bg-background py-4">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-3 gap-y-1 px-4 text-xs text-muted lg:max-w-[96rem]">
          {!IST_PRODUKTION && (
            <span className="inline-flex items-center rounded-full border border-warning-ring bg-warning-bg px-2 py-0.5 font-medium text-warning-strong">
              {import.meta.env.MODE}
            </span>
          )}
          <span>Linnenbecker Altwaren-Suche</span>
          <span className="text-subtle">
            Frontend-Build{" "}
            {BUILD_ZEIT.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}
          </span>

          {/*
           * Eigene Landmarke und nicht bloß zwei Links: `aria-label` macht aus
           * ihnen eine benannte Gruppe, die ein Screenreader ansteuern kann,
           * ohne den Fuß Wort für Wort zu lesen. Der Bestand führt dieselbe
           * (`<nav aria-label="Rechtliches">`).
           *
           * `sm:ml-auto` erst ab `sm`: auf dem Telefon bricht die Zeile
           * ohnehin um (`flex-wrap` am Container), und ein `ml-auto` schöbe
           * die beiden dann in eine eigene, halb leere Zeile.
           *
           * `target="_blank"`: die Texte gehören linnenbecker.de und liegen
           * außerhalb dieser Anwendung. Wer sie in DIESEM Tab öffnete,
           * verlöre den Zustand der Suche — und der ist in dieser App die URL
           * selbst. `rel="noreferrer noopener"` gehört dazu; ohne `noopener`
           * bekäme die fremde Seite ein `window.opener` auf uns.
           */}
          <nav aria-label="Rechtliches" className="flex items-center gap-x-3 sm:ml-auto">
            <a
              href={IMPRESSUM_URL}
              target="_blank"
              rel="noreferrer noopener"
              className={RECHTLICHES_KLASSEN}
            >
              Impressum
            </a>
            <a
              href={DATENSCHUTZ_URL}
              target="_blank"
              rel="noreferrer noopener"
              className={RECHTLICHES_KLASSEN}
            >
              Datenschutz
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
